import { ChangeEvent, use, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PickersInputComponentLocaleText } from '@mui/x-date-pickers';
import { Moment } from 'moment';
import { validateTimeFrames } from 'shared/utils/timeframe-utils';

import TimeFrame from 'features/residents/models/configurations/time-frame';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type Props = {
  timeFrames: TimeFrame[] | null;
  activeAllDay: boolean;
  valuesChanged: (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean) => void;
  index: number;
};

export const useTimeFrames = ({ timeFrames, activeAllDay, valuesChanged, index }: Props) => {
  const { t } = useTranslation('residentsSettings');
  const [validationErrors, setValidationErrors] = useState<boolean[]>([]);
  const {
    detailEditState: { viewingMode, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);
  const { formState } = useFormContext();

  const addTimeFrame = () => {
    const newTimeFrames = [...(timeFrames ?? [])];
    newTimeFrames.push({
      from: '00:00:00.000',
      to: '00:00:00.000',
    });

    valuesChanged(newTimeFrames, activeAllDay);
  };

  const removeTimeFrame = (frameIndex: number) => {
    const newTimeFrames = [...(timeFrames ?? [])];
    newTimeFrames.splice(frameIndex, 1);

    valuesChanged(newTimeFrames, activeAllDay);
  };

  const onTimeFrameChanged = (newValue: Moment | null, frameIndex: number, isFromField: boolean) => {
    const newTimeFrames = [...(timeFrames ?? [])];

    const formattedValue = newValue?.format('HH:mm:ss') ?? '';
    if (isFromField) {
      newTimeFrames[frameIndex].from = formattedValue;
    } else {
      newTimeFrames[frameIndex].to = formattedValue;
    }

    valuesChanged(newTimeFrames, activeAllDay);
  };

  const validate = (newTimeFrames: TimeFrame[]) => {
    const newValidationErrors = validateTimeFrames(newTimeFrames);

    setValidationErrors(newValidationErrors);

    if (newValidationErrors.every((x) => !x)) {
      residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    } else {
      residentSettingEditForm.setError(`organisationUnitScenarios.${index}`, { type: 'pattern' });
    }
  };

  const setAllDayTimeFrame = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!checked) {
      const newTimeFrames = [...(timeFrames ?? [])];
      if (newTimeFrames.length === 0) {
        newTimeFrames.push({
          from: '00:00:00.000',
          to: '00:00:00.000',
        });
      }
      valuesChanged(newTimeFrames, checked);
    } else {
      valuesChanged([], checked);
    }
  };

  const timePickerLocaleText: PickersInputComponentLocaleText = {
    fieldHoursPlaceholder: () => t('timePickerLocale.fieldHoursPlaceholder'),
    fieldMinutesPlaceholder: () => t('timePickerLocale.fieldMinutesPlaceholder'),
  };

  useEffect(() => {
    if (formState.isSubmitting && timeFrames != null) {
      validate(timeFrames);
    }
  }, [formState.isSubmitting, timeFrames]);

  useEffect(() => {
    setValidationErrors([]);
  }, [viewingMode]);

  return {
    viewingMode,
    validationErrors,
    timePickerLocaleText,
    addTimeFrame,
    removeTimeFrame,
    onTimeFrameChanged,
    setAllDayTimeFrame,
  };
};
